import {
  GlobalSettings,
  ServerRoutingTarget,
  getServerRoutingOverride,
  isSameServerRoutingTarget,
  normalizeServerRoutingTarget,
  setServerRoutingOverride,
} from "../global_settings.js";
import { ScreenManager, Screens } from "../screen_manager/screen_manager.js";
import { ensureDeviceId } from "../utils/device_id.js";
import { VersionLoader } from "../version_loader.js";
import { detectPlatform } from "../api/http_client.js";
import { DialogManager } from "../ui/dialog/dialog_manager.js";
import { OverlayManager } from "../ui/overlay/overlay_manager.js";
import { ServerChangedDialog } from "../ui/dialog/server_changed_dialog.js";

export type ServerRoutingCheckReason = 'startup' | 'login_failed' | 'foreground' | 'manual';

export interface ServerRoutingCheckResult
{
  checked: boolean;
  changed: boolean;
  target: ServerRoutingTarget | null;
  server_key?: string | null;
  rule_id?: string | null;
  transfer?: string | null;
  error?: string;
}

interface RoutingDecision
{
  target: ServerRoutingTarget | null;
  server_key: string | null;
  rule_id: string | null;
  transfer: string | null;
}

// Asks the build's home server (never the currently applied override) where
// this client should connect. A changed answer is stored and applied by a
// reload: immediately while the game is still starting up, otherwise through a
// restart dialog so a running session is not torn down under the player.
export class ServerRoutingService
{
  // The home server may copy the account to the target inside this request.
  private static readonly REQUEST_TIMEOUT_MS = 20_000;
  private static readonly HEALTH_TIMEOUT_MS = 5_000;
  private static readonly STALE_AFTER_MS = 5 * 60_000;
  private static readonly DIALOG_RETRY_MS = 1_000;

  private static startup_complete = false;
  private static inflight: Promise<ServerRoutingCheckResult> | null = null;
  private static reload_scheduled = false;
  private static dialog_pending = false;
  private static screen_hook_installed = false;
  private static dialog_retry_timer: ReturnType<typeof setInterval> | null = null;
  private static last_result: ServerRoutingCheckResult | null = null;
  private static last_checked_at = 0;

  public static markStartupComplete(): void
  {
    this.startup_complete = true;
  }

  public static getLastResult(): ServerRoutingCheckResult | null
  {
    return this.last_result;
  }

  public static getLastCheckedAt(): number
  {
    return this.last_checked_at;
  }

  // Long-running sessions re-check on return to the foreground, so a redirect
  // set while the game sat in the background still reaches the player without
  // waiting for the next cold start.
  public static checkIfStale( reason: ServerRoutingCheckReason ): Promise<ServerRoutingCheckResult | null>
  {
    if ( Date.now() - this.last_checked_at < this.STALE_AFTER_MS )
      return Promise.resolve( null );
    return this.check( reason );
  }

  public static check( reason: ServerRoutingCheckReason ): Promise<ServerRoutingCheckResult>
  {
    if ( this.inflight ) return this.inflight;

    this.inflight = this.run( reason )
      .catch( ( error ): ServerRoutingCheckResult =>
      {
        const message = error instanceof Error ? error.message : String( error );
        console.warn( `[ServerRouting] check failed (${reason}): ${message}` );
        return { checked: false, changed: false, target: getServerRoutingOverride(), error: message };
      } )
      .then( result =>
      {
        this.last_result = result;
        this.last_checked_at = Date.now();
        this.inflight = null;
        return result;
      } );

    return this.inflight;
  }

  private static async run( reason: ServerRoutingCheckReason ): Promise<ServerRoutingCheckResult>
  {
    const current = getServerRoutingOverride();
    const decision = await this.fetchDecision();
    const { target, server_key, rule_id, transfer } = decision;

    if ( isSameServerRoutingTarget( current, target ) )
    {
      console.info( `[ServerRouting] unchanged (${reason}): ${target ? `${target.api_url} via ${rule_id}` : 'home'}${transfer && transfer !== 'none' ? ` (transfer ${transfer})` : ''}` );
      return { checked: true, changed: false, target, server_key, rule_id, transfer };
    }

    if ( target )
    {
      const healthy = await this.isHealthy( target.api_url );
      if ( !healthy )
      {
        console.warn( `[ServerRouting] target ${target.api_url} failed health check, keeping ${current ? current.api_url : 'home'}` );
        return { checked: true, changed: false, target: current, server_key, rule_id, transfer, error: 'target unreachable' };
      }
    }

    setServerRoutingOverride( target );
    console.info( `[ServerRouting] switched (${reason}): ${current ? current.api_url : 'home'} -> ${target ? `${target.api_url} via ${rule_id}` : 'home'} (account ${transfer === 'done' ? 'transferred' : 'not transferred'})` );
    this.apply();
    return { checked: true, changed: true, target, server_key, rule_id, transfer };
  }

  // The home server matches rules on device id, country (from the IP),
  // app version and platform; the client only has to identify itself.
  private static async fetchDecision(): Promise<RoutingDecision>
  {
    const params = new URLSearchParams( {
      device_id: await ensureDeviceId(),
      app_version: VersionLoader.getVersion(),
      platform: detectPlatform(),
    } );
    const url = `${GlobalSettings.HOME_API_BASE_URL}/api/routing?${params.toString()}`;
    const response = await this.fetchWithTimeout( url, this.REQUEST_TIMEOUT_MS );
    if ( !response.ok )
      throw new Error( `HTTP ${response.status} from ${url}` );

    const body = await response.json() as { target?: unknown; server_key?: unknown; rule_id?: unknown; transfer?: unknown; transfer_error?: unknown };
    const server_key = typeof body.server_key === 'string' ? body.server_key : null;
    const rule_id = typeof body.rule_id === 'string' ? body.rule_id : null;
    const transfer = typeof body.transfer === 'string' ? body.transfer : null;
    if ( typeof body.transfer_error === 'string' )
      console.warn( `[ServerRouting] home server could not move the account: ${body.transfer_error}` );
    if ( body.target === null || body.target === undefined )
      return { target: null, server_key, rule_id, transfer };

    const target = normalizeServerRoutingTarget( body.target );
    if ( !target )
      throw new Error( 'routing response rejected: invalid target' );
    return { target, server_key, rule_id, transfer };
  }

  private static async isHealthy( api_url: string ): Promise<boolean>
  {
    try
    {
      const response = await this.fetchWithTimeout( `${api_url}/health`, this.HEALTH_TIMEOUT_MS );
      if ( !response.ok ) return false;
      const body = await response.json() as { status?: unknown };
      return body.status === 'OK';
    }
    catch
    {
      return false;
    }
  }

  private static async fetchWithTimeout( url: string, timeout_ms: number ): Promise<Response>
  {
    const controller = new AbortController();
    const timer = setTimeout( () => controller.abort(), timeout_ms );
    try
    {
      return await fetch( url, { signal: controller.signal, cache: 'no-store' } );
    }
    finally
    {
      clearTimeout( timer );
    }
  }

  private static apply(): void
  {
    if ( !this.startup_complete )
    {
      this.reload();
      return;
    }
    this.showDialogWhenPossible();
  }

  public static reload(): void
  {
    if ( this.reload_scheduled ) return;
    this.reload_scheduled = true;
    window.location.reload();
  }

  // A battle or a tutorial overlay (which sits above the dialog canvas) makes
  // the dialog either unwanted or unreachable, so it waits for a screen change
  // and polls until the way is clear.
  private static showDialogWhenPossible(): void
  {
    this.dialog_pending = true;
    if ( this.tryOpenDialog() ) return;

    if ( this.dialog_retry_timer === null )
      this.dialog_retry_timer = setInterval( () => this.tryOpenDialog(), this.DIALOG_RETRY_MS );

    if ( this.screen_hook_installed ) return;
    this.screen_hook_installed = true;
    ScreenManager.addOnChangeListener( () => this.tryOpenDialog() );
  }

  private static tryOpenDialog(): boolean
  {
    if ( !this.dialog_pending ) return false;
    if ( !DialogManager.ctx
      || ScreenManager.getCurrentScreen() === Screens.GAME
      || OverlayManager.isAnyOverlayOpened()
      || DialogManager.isAnyDialogOpened() )
      return false;

    this.dialog_pending = false;
    if ( this.dialog_retry_timer !== null )
    {
      clearInterval( this.dialog_retry_timer );
      this.dialog_retry_timer = null;
    }
    DialogManager.openDialog( new ServerChangedDialog() );
    return true;
  }
}
